import { apiClient } from '@/lib/api';
import type {
  ApprovalWorkflowConfig,
  ApprovalMode,
  ApprovalRule,
  CreateRulePayload,
  ApproverUser,
  CargoDisponible,
  PasoAprobadorPedido,
  WatcherUser,
  LobObserverRow,
} from '../types/approval-workflow.types';

const BASE = '/api/approval-workflow';

export const approvalWorkflowService = {
  getConfig: async (): Promise<ApprovalWorkflowConfig> => {
    return apiClient.get(BASE);
  },

  updateMode: async (defaultMode: ApprovalMode): Promise<ApprovalWorkflowConfig> => {
    return apiClient.put(BASE, { defaultMode });
  },

  /** Si aplican varias reglas a la vez, que firmen los aprobadores de todas. */
  updateAcumular: async (
    defaultMode: ApprovalMode,
    acumularAprobadores: boolean
  ): Promise<ApprovalWorkflowConfig> => {
    return apiClient.put(BASE, { defaultMode, acumularAprobadores });
  },

  addRule: async (data: CreateRulePayload): Promise<ApprovalRule> => {
    return apiClient.post(`${BASE}/rules`, data);
  },

  updateRule: async (id: string, data: Partial<CreateRulePayload>): Promise<ApprovalRule> => {
    return apiClient.put(`${BASE}/rules/${id}`, data);
  },

  deleteRule: async (id: string): Promise<void> => {
    return apiClient.delete(`${BASE}/rules/${id}`);
  },

  /**
   * Guarda la cadena de aprobación de una regla.
   *
   * El orden del arreglo define la secuencia de firmas. Cada paso lleva una
   * persona o un cargo — el backend rechaza un paso con los dos o con ninguno.
   */
  setRuleApprovers: async (
    ruleId: string,
    approvers: PasoAprobadorPedido[]
  ): Promise<ApprovalRule> => {
    return apiClient.put(`${BASE}/rules/${ruleId}/approvers`, { approvers });
  },

  /** Los cargos de la empresa, para poder armar la cadena por cargo. */
  getCargos: async (): Promise<CargoDisponible[]> => {
    return apiClient.get('/api/custom-roles');
  },

  getApproverUsers: async (): Promise<ApproverUser[]> => {
    return apiClient.get(`${BASE}/approver-users`);
  },

  /** Candidatos a observador: cualquier persona activa de la empresa (no hace falta que apruebe). */
  getWatcherUsers: async (): Promise<WatcherUser[]> => {
    return apiClient.get(`${BASE}/watcher-users`);
  },

  /** Los observadores de todas las líneas de producto de la empresa. */
  getLobObservers: async (): Promise<LobObserverRow[]> => {
    return apiClient.get(`${BASE}/lob-observers`);
  },

  /** Reemplaza los observadores de UNA línea. Una persona o un cargo por fila. */
  setLobObservers: async (
    lobId: string,
    observers: PasoAprobadorPedido[]
  ): Promise<LobObserverRow[]> => {
    return apiClient.put(`${BASE}/lob-observers/${lobId}`, { observers });
  },
};
